import React, { useEffect, useState } from "react";
import { Search, Loader2, AlertCircle, CheckCircle2, Info, AlertTriangle, ExternalLink, Wallet, Sparkles } from "lucide-react";
import { toast } from "sonner";
import { Link } from "react-router-dom";
import { api } from "../../lib/api";

const SEVERITY_META = {
  critical: { Icon: AlertCircle, color: "text-rose-400", bg: "bg-rose-500/10", border: "border-rose-500/30" },
  warn:     { Icon: AlertTriangle, color: "text-amber-400", bg: "bg-amber-500/10", border: "border-amber-500/30" },
  info:     { Icon: Info, color: "text-sky-400", bg: "bg-sky-500/10", border: "border-sky-500/30" },
};

function ScoreRing({ score }) {
  const tier = score >= 85 ? "text-emerald-400 border-emerald-500/40" :
               score >= 65 ? "text-sky-400 border-sky-500/40" :
               score >= 45 ? "text-amber-400 border-amber-500/40" :
                             "text-rose-400 border-rose-500/40";
  return (
    <div className={`w-28 h-28 rounded-full border-4 ${tier} flex flex-col items-center justify-center bg-slate-900/60`}
         data-testid="seo-score-ring">
      <div className={`text-3xl font-black ${tier}`}>{score}</div>
      <div className="text-[10px] text-slate-400 uppercase tracking-wider">SEO score</div>
    </div>
  );
}

function Stat({ label, value, sub }) {
  return (
    <div className="card p-3 text-center">
      <div className="text-xs text-slate-400 uppercase tracking-wider">{label}</div>
      <div className="text-xl font-bold text-white mt-1">{value}</div>
      {sub && <div className="text-[11px] text-slate-500 mt-0.5">{sub}</div>}
    </div>
  );
}

export default function SEOAnalyzer() {
  const [url, setUrl] = useState("");
  const [busy, setBusy] = useState(false);
  const [result, setResult] = useState(null);
  const [wallet, setWallet] = useState(null);
  const [history, setHistory] = useState([]);

  const COST = 2;

  const load = async () => {
    try {
      const [w, h] = await Promise.all([
        api.get("/credits/me").then(r => r.data),
        api.get("/seo/history?kind=analyze&limit=10").then(r => r.data),
      ]);
      setWallet(w); setHistory(h);
    } catch { /* silent */ }
  };
  useEffect(() => { load(); }, []);

  const analyze = async () => {
    if (!url.trim()) { toast.error("Enter a URL"); return; }
    if (wallet && wallet.balance < COST) {
      toast.error("Not enough credits — top up your wallet"); return;
    }
    setBusy(true); setResult(null);
    try {
      const { data } = await api.post("/seo/analyze", { url: url.trim() });
      setResult(data.result);
      toast.success(`Analysis complete — score ${data.score}/100`);
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Audit failed");
    } finally { setBusy(false); }
  };

  return (
    <div className="space-y-6" data-testid="seo-analyzer-page">
      <header className="flex items-start justify-between gap-4 flex-wrap">
        <div>
          <h1 className="text-3xl font-black text-white flex items-center gap-3" style={{fontFamily:'Outfit'}}>
            <Search className="text-blue-400" /> Website Analyzer
          </h1>
          <p className="text-slate-400 mt-1 text-sm">Technical SEO audit, on-page signals, scoring & AI fixes — {COST} credits / run.</p>
        </div>
        <Link to="/dashboard/credits" className="card px-4 py-2.5 flex items-center gap-2 text-sm text-slate-300 hover:text-white" data-testid="seo-credits-balance">
          <Wallet size={15} className="text-blue-400" />
          <span className="font-semibold text-white">{wallet?.balance ?? "—"}</span> credits
        </Link>
      </header>

      <div className="card p-5 flex gap-3 flex-wrap items-center">
        <input
          type="text"
          value={url}
          onChange={(e) => setUrl(e.target.value)}
          onKeyDown={(e) => e.key === "Enter" && !busy && analyze()}
          placeholder="https://example.com  or  example.com"
          className="flex-1 min-w-[280px] bg-slate-950/80 border border-slate-700 focus:border-blue-500 outline-none rounded-lg px-4 py-2.5 text-white placeholder:text-slate-500"
          data-testid="seo-analyze-url-input"
        />
        <button onClick={analyze} disabled={busy} className="btn-primary" data-testid="seo-analyze-btn">
          {busy ? <><Loader2 className="animate-spin" size={16}/> Auditing…</> :
                   <><Search size={16}/> Run Audit · {COST} cr</>}
        </button>
      </div>

      {result && (
        <div className="space-y-5" data-testid="seo-analyze-result">
          <div className="card p-6 flex items-center gap-6 flex-wrap">
            <ScoreRing score={result.score} />
            <div className="flex-1 min-w-[260px] space-y-1">
              <div className="text-xs text-slate-400 uppercase tracking-wider">Audited URL</div>
              <a href={result.url} target="_blank" rel="noreferrer" className="text-white font-semibold break-all flex items-center gap-2 hover:text-blue-400">
                {result.url} <ExternalLink size={14}/>
              </a>
              <div className="text-xs text-slate-500">HTTP {result.status} · {result.elapsed_ms} ms · {Math.round(result.html_bytes/1024)} KB</div>
              {result.ai_summary && (
                <div className="mt-3 p-3 rounded-lg bg-blue-500/5 border border-blue-500/20 text-sm text-slate-200 whitespace-pre-line">
                  <div className="flex items-center gap-2 text-blue-300 text-xs font-semibold mb-1.5"><Sparkles size={12}/> AI Consultant Summary</div>
                  {result.ai_summary}
                </div>
              )}
            </div>
          </div>

          <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
            <Stat label="Title length" value={result.title_length} sub={`${result.title_length>=50 && result.title_length<=60 ? "ideal" : "out of 50–60"}`}/>
            <Stat label="Meta desc" value={result.meta_description_length || "—"} sub={result.meta_description ? "set" : "missing"}/>
            <Stat label="H1 / H2 / H3" value={`${result.h_counts.h1} / ${result.h_counts.h2} / ${result.h_counts.h3}`}/>
            <Stat label="Images missing alt" value={`${result.images.missing_alt}/${result.images.total}`}/>
            <Stat label="Internal links" value={result.links.internal_count}/>
            <Stat label="External links" value={result.links.external_count}/>
            <Stat label="Word count" value={result.word_count}/>
            <Stat label="Indexable" value={result.indexable ? "Yes" : "No"} sub={result.robots || "—"}/>
          </div>

          <div className="card p-5">
            <h3 className="text-white font-bold text-lg mb-3 flex items-center gap-2">
              <CheckCircle2 className="text-emerald-400" size={18}/> Issues ({result.issues.length})
            </h3>
            {result.issues.length === 0 ? (
              <div className="text-emerald-300 text-sm">No issues — flawless audit.</div>
            ) : (
              <ul className="space-y-2" data-testid="seo-issues-list">
                {result.issues.map((iss, i) => {
                  const m = SEVERITY_META[iss.severity] || SEVERITY_META.info;
                  return (
                    <li key={i} className={`flex items-start gap-3 p-3 rounded-lg ${m.bg} border ${m.border}`}>
                      <m.Icon size={16} className={`${m.color} flex-shrink-0 mt-0.5`}/>
                      <div className="flex-1">
                        <div className="text-white text-sm">{iss.message}</div>
                        <div className="text-[11px] text-slate-500 mt-0.5">{iss.key} · −{iss.deducted} pts</div>
                      </div>
                    </li>
                  );
                })}
              </ul>
            )}
          </div>

          <div className="grid md:grid-cols-2 gap-4">
            <div className="card p-5">
              <h4 className="text-white font-semibold mb-2 text-sm">Open Graph</h4>
              {Object.keys(result.og || {}).length === 0 ? (
                <div className="text-slate-500 text-sm">No OG tags.</div>
              ) : (
                <dl className="text-xs space-y-1">
                  {Object.entries(result.og).slice(0, 8).map(([k, v]) => (
                    <div key={k} className="flex gap-2">
                      <dt className="text-slate-400 min-w-[120px]">{k}</dt>
                      <dd className="text-slate-200 break-all">{String(v).slice(0, 100)}</dd>
                    </div>
                  ))}
                </dl>
              )}
            </div>
            <div className="card p-5">
              <h4 className="text-white font-semibold mb-2 text-sm">Site files</h4>
              <ul className="text-xs space-y-1.5">
                <li className="flex items-center gap-2 text-slate-300">
                  {result.aux?.robots_txt?.found ? <CheckCircle2 size={14} className="text-emerald-400"/> : <AlertCircle size={14} className="text-rose-400"/>}
                  /robots.txt {result.aux?.robots_txt?.found ? "found" : "missing"}
                </li>
                <li className="flex items-center gap-2 text-slate-300">
                  {result.aux?.sitemap_xml?.found ? <CheckCircle2 size={14} className="text-emerald-400"/> : <AlertCircle size={14} className="text-rose-400"/>}
                  /sitemap.xml {result.aux?.sitemap_xml?.found ? "found" : "missing"}
                </li>
                <li className="flex items-center gap-2 text-slate-300">
                  {result.viewport ? <CheckCircle2 size={14} className="text-emerald-400"/> : <AlertCircle size={14} className="text-amber-400"/>}
                  Viewport meta {result.viewport ? "set" : "missing"}
                </li>
                <li className="flex items-center gap-2 text-slate-300">
                  {result.canonical ? <CheckCircle2 size={14} className="text-emerald-400"/> : <Info size={14} className="text-sky-400"/>}
                  Canonical {result.canonical ? "set" : "missing"}
                </li>
                <li className="flex items-center gap-2 text-slate-300">
                  {result.schema_blocks > 0 ? <CheckCircle2 size={14} className="text-emerald-400"/> : <Info size={14} className="text-sky-400"/>}
                  Schema.org JSON-LD ({result.schema_blocks})
                </li>
              </ul>
            </div>
          </div>
        </div>
      )}

      {history.length > 0 && (
        <div className="card p-5">
          <h3 className="text-white font-bold mb-3 text-sm uppercase tracking-wider">Recent audits</h3>
          <ul className="divide-y divide-slate-800" data-testid="seo-history-list">
            {history.map(h => (
              <li key={h.id} className="py-2.5 flex items-center justify-between gap-3 text-sm">
                <div className="text-slate-300 truncate flex-1">{h.url}</div>
                <div className={`px-2 py-0.5 rounded text-xs font-bold ${h.score>=65 ? "bg-emerald-500/15 text-emerald-300" : h.score>=45 ? "bg-amber-500/15 text-amber-300" : "bg-rose-500/15 text-rose-300"}`}>{h.score}</div>
                <div className="text-xs text-slate-500 hidden sm:block">{new Date(h.created_at).toLocaleString()}</div>
              </li>
            ))}
          </ul>
        </div>
      )}
    </div>
  );
}
